import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { useToast } from "../components/ToastProvider";

function combinePreferred(booking) {
  if (!booking?.preferredDate) return "";
  const time = booking?.preferredTime ? String(booking.preferredTime).slice(0, 5) : "09:00";
  return `${booking.preferredDate}T${time}`;
}

export default function Jobs() {
  const toast = useToast();
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [assignForm, setAssignForm] = useState({ employeeId: "", scheduledAt: "" });
  const [actionLoading, setActionLoading] = useState(false);

  const selectedJob = useMemo(
    () => jobs.find((j) => Number(j.serviceId) === Number(selectedId)) || null,
    [jobs, selectedId]
  );

  const loadEmployees = async () => {
    try {
      const response = await api.get("/api/admin/employees");
      setEmployees(response?.data?.data || []);
    } catch {
      setEmployees([]);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/admin/jobs");
      const list = response?.data?.data || [];
      setJobs(list);
      if (selectedId && !list.some((j) => Number(j.serviceId) === Number(selectedId))) {
        setSelectedId(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedJob) return;
    setAssignForm({
      employeeId: selectedJob.employeeId ? String(selectedJob.employeeId) : "",
      scheduledAt: combinePreferred(selectedJob),
    });
  }, [selectedJob]);

  const onAssignChange = (e) => {
    setAssignForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const assignEmployee = async () => {
    if (!selectedJob) return;
    if (!assignForm.employeeId) {
      toast.error("Select an employee to assign.");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/api/admin/jobs/${selectedJob.serviceId}/assign`, {
        employeeId: Number(assignForm.employeeId),
        scheduledAt: assignForm.scheduledAt || null,
      });
      toast.success("Employee assigned and email sent.");
      await loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to assign employee");
    } finally {
      setActionLoading(false);
    }
  };

  const markFinished = async () => {
    if (!selectedJob) return;
    setActionLoading(true);
    try {
      await api.post(`/api/admin/jobs/${selectedJob.serviceId}/status`, { status: "finished" });
      toast.success("Job marked as finished.");
      await loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update job status");
    } finally {
      setActionLoading(false);
    }
  };

  const sendReminder = async () => {
    if (!selectedJob) return;
    setActionLoading(true);
    try {
      await api.post(`/api/admin/jobs/${selectedJob.serviceId}/remind`);
      toast.success("Reminder emails sent to client and employee.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send reminders");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="rs-page">
      <div className="rs-page__header">
        <div>
          <h1 className="rs-h1">Jobs</h1>
          <div className="rs-muted">Paid bookings (Step 3) that can be assigned and completed</div>
        </div>
        <button className="rs-btn rs-btn--outline" onClick={loadJobs} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="rs-grid rs-grid--two rs-grid--jobs">
        <div className="rs-card">
          <div className="rs-card__header rs-card__header--row">
            <div className="rs-card__title">Job list</div>
            <div className="rs-muted">{jobs.length} jobs</div>
          </div>

          <div className="rs-table-wrap">
            <table className="rs-table rs-table--clickable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Client</th>
                  <th>Preferred</th>
                  <th>Get In</th>
                  <th>Parking</th>
                  <th>Bathrooms</th>
                  <th>Carpet</th>
                  <th>Extra Hours</th>
                  <th>Charge</th>
                  <th>Cleaning Hrs (Aprox.)</th>
                  <th>Employee</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="rs-empty">
                      {loading ? "Loading..." : "No jobs found"}
                    </td>
                  </tr>
                ) : (
                  jobs.map((j) => (
                    <tr
                      key={j.serviceId}
                      className={Number(selectedId) === Number(j.serviceId) ? "is-selected" : ""}
                      onClick={() => setSelectedId(j.serviceId)}
                    >
                      <td data-label="ID">
                        <div className="rs-td__value">{j.serviceId}</div>
                      </td>
                      <td data-label="Client">
                        <div className="rs-td__value">{`${j.firstName || ""} ${j.lastName || ""}`.trim()}</div>
                      </td>
                      <td data-label="Preferred">
                        <div className="rs-td__value">
                          {j.preferredDate
                            ? `${j.preferredDate} ${String(j.preferredTime || "").slice(0, 5)}`
                            : "-"}
                        </div>
                      </td>
                      <td data-label="Get In">
                        <div className="rs-td__value">{j.getInOption || <span className="rs-muted">-</span>}</div>
                      </td>
                      <td data-label="Parking">
                        <div className="rs-td__value">{j.parkOption || <span className="rs-muted">-</span>}</div>
                      </td>
                      <td data-label="Bathrooms">
                        <div className="rs-td__value">
                          {typeof j.bathrooms !== "undefined" && j.bathrooms !== null ? j.bathrooms : <span className="rs-muted">-</span>}
                        </div>
                      </td>
                      <td data-label="Carpet">
                        <div className="rs-td__value">
                          {j.carpetOptions || <span className="rs-muted">-</span>}
                        </div>
                      </td>
                      <td data-label="Extra Hours">
                        <div className="rs-td__value">
                          {j.extraHours || j.ExtraHours || <span className="rs-muted">-</span>}
                        </div>
                      </td>
                      <td data-label="Charge">
                        <div className="rs-td__value">
                          {(() => {
                            const raw = j.charge ?? j.Charge;
                            if (raw === null || typeof raw === "undefined" || raw === "") return <span className="rs-muted">-</span>;
                            const value = Number(raw);
                            return Number.isFinite(value) ? `$${value.toFixed(2)}` : String(raw);
                          })()}
                        </div>
                      </td>
                      <td data-label="Cleaning Hrs (Aprox.)">
                        <div className="rs-td__value">
                          {j.cleaningHoursApprox ? `${j.cleaningHoursApprox} hrs` : <span className="rs-muted">-</span>}
                        </div>
                      </td>
                      <td data-label="Employee">
                        <div className="rs-td__value">
                          {j.employeeName ? (
                            j.employeeName
                          ) : (
                            <span className="rs-pill rs-pill--muted">Unassigned</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Status">
                        <div className="rs-td__value">
                          {j.jobStatus ? <span className="rs-pill rs-pill--info">{j.jobStatus}</span> : "-"}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rs-card">
          <div className="rs-card__header">
            <div className="rs-card__title">Job details</div>
            <div className="rs-card__subtitle">Assign employee, update status, send reminders</div>
          </div>

          {!selectedJob ? (
            <div className="rs-empty rs-empty--panel">Select a job to manage</div>
          ) : (
            <div className="rs-job">
              <div className="rs-job__meta">
                <div className="rs-kv">
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Job</div>
                    <div className="rs-kv__v">#{selectedJob.serviceId}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Client</div>
                    <div className="rs-kv__v">{`${selectedJob.firstName || ""} ${selectedJob.lastName || ""}`.trim()}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Email</div>
                    <div className="rs-kv__v">{selectedJob.email}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Phone</div>
                    <div className="rs-kv__v">{selectedJob.phone}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Address</div>
                    <div className="rs-kv__v">
                      {[selectedJob.streetAddress, selectedJob.suburb, selectedJob.postcode]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">How can we get in</div>
                    <div className="rs-kv__v">{selectedJob.getInOption || "-"}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Where can we park</div>
                    <div className="rs-kv__v">{selectedJob.parkOption || "-"}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Bathrooms</div>
                    <div className="rs-kv__v">
                      {typeof selectedJob.bathrooms !== "undefined" && selectedJob.bathrooms !== null
                        ? selectedJob.bathrooms
                        : "-"}
                    </div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Preferred</div>
                    <div className="rs-kv__v">
                      {selectedJob.preferredDate
                        ? `${selectedJob.preferredDate} ${String(selectedJob.preferredTime || "").slice(0, 5)}`
                        : "-"}
                    </div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Extra hours</div>
                    <div className="rs-kv__v">{selectedJob.extraHours || selectedJob.ExtraHours || "-"}</div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Charge</div>
                    <div className="rs-kv__v">
                      {(() => {
                        const raw = selectedJob.charge ?? selectedJob.Charge;
                        if (raw === null || typeof raw === "undefined" || raw === "") return "-";
                        const value = Number(raw);
                        return Number.isFinite(value) ? `$${value.toFixed(2)}` : String(raw);
                      })()}
                    </div>
                  </div>
                  <div className="rs-kv__row">
                    <div className="rs-kv__k">Cleaning hours (Aprox.)</div>
                    <div className="rs-kv__v">
                      {selectedJob.cleaningHoursApprox ? `${selectedJob.cleaningHoursApprox} hrs` : "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rs-divider" />

              <div>
                <div className="rs-section-title">
                  {selectedJob.employeeId ? "Change cleaner" : "Assign cleaner"}
                </div>
                <div className="rs-form-row">
                  <label className="rs-field">
                    <span>Employee</span>
                    <select
                      className="rs-input"
                      name="employeeId"
                      value={assignForm.employeeId}
                      onChange={onAssignChange}
                    >
                      <option value="">Select...</option>
                      {employees.map((e) => (
                        <option key={e.empid} value={e.empid}>
                          {e.name} ({e.email})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="rs-field">
                    <span>Scheduled date/time</span>
                    <input
                      className="rs-input"
                      name="scheduledAt"
                      type="datetime-local"
                      value={assignForm.scheduledAt}
                      onChange={onAssignChange}
                    />
                  </label>
                </div>
                {selectedJob.employeeId && (
                  <div className="rs-muted" style={{ marginBottom: 12 }}>
                    Reassigning will replace the current cleaner and send a new assignment email.
                  </div>
                )}
                <button className="rs-btn rs-btn--primary" onClick={assignEmployee} disabled={actionLoading}>
                  {actionLoading
                    ? selectedJob.employeeId
                      ? "Updating..."
                      : "Assigning..."
                    : selectedJob.employeeId
                      ? "Update assignment"
                      : "Assign cleaner"}
                </button>
              </div>

              {selectedJob.employeeId && (
                <>
                  <div className="rs-divider" />
                  <div>
                    <div className="rs-section-title">Current assignment</div>
                    <div className="rs-kv">
                      <div className="rs-kv__row">
                        <div className="rs-kv__k">Employee</div>
                        <div className="rs-kv__v">{selectedJob.employeeName}</div>
                      </div>
                      <div className="rs-kv__row">
                        <div className="rs-kv__k">Employee Email</div>
                        <div className="rs-kv__v">{selectedJob.employeeEmail}</div>
                      </div>
                      <div className="rs-kv__row">
                        <div className="rs-kv__k">Job status</div>
                        <div className="rs-kv__v">{selectedJob.jobStatus || "assigned"}</div>
                      </div>
                    </div>

                    <div className="rs-actions">
                      <button className="rs-btn rs-btn--outline" onClick={sendReminder} disabled={actionLoading}>
                        {actionLoading ? "Sending..." : "Send reminder emails"}
                      </button>
                      <button
                        className="rs-btn rs-btn--primary"
                        onClick={markFinished}
                        disabled={actionLoading || selectedJob.jobStatus === "finished"}
                      >
                        {selectedJob.jobStatus === "finished" ? "Finished" : actionLoading ? "Updating..." : "Mark as finished"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
